import React from 'react';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import { Box, Grid, Typography } from '@mui/material';
import ForumIcon from '@mui/icons-material/Forum';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import AddLinkIcon from '@mui/icons-material/AddLink';
import PaidIcon from '@mui/icons-material/Paid';
import StatCard from './common/StatCard';
import { useLiveActivity } from '../hooks/useLiveActivity';
import { bookedCallsNote, formatUsd, waitingSince } from '../utils/liveActivity';

/**
 * Turn the live-activity payload into tile props. A half the server could not
 * read shows a dash and the reason: on this dashboard a 0 has meant "broken"
 * as often as "none", so the two must not look alike.
 */
export function buildTiles({ data, error }) {
  const loading = !data && !error;
  const connections = data?.connections;
  const cashouts = data?.cashouts;

  const unavailable = (half) => {
    if (loading) return { value: '…', helpText: 'Loading' };
    const reason = error || half?.error || 'no data';
    return { value: '—', helpText: `Unavailable: ${reason}`, color: 'error' };
  };
  const connectionsDown = !connections?.ok;
  const cashoutsDown = !cashouts?.ok;
  const windowDays = connections?.windowDays ?? 7;

  const owed = [
    cashouts?.totalCents != null ? `${formatUsd(cashouts.totalCents)} owed` : null,
    waitingSince(cashouts?.oldestRequestedAt),
  ].filter(Boolean).join(' · ');

  return [
    {
      key: 'active',
      to: '/connections',
      title: 'Active connections',
      icon: <ForumIcon />,
      ...(connectionsDown ? unavailable(connections) : {
        value: connections.active,
        helpText: `Fans in conversation with a creator right now${bookedCallsNote(connections.bookedCalls)}`,
        color: 'success',
      }),
    },
    {
      key: 'awaiting',
      to: '/connections',
      title: 'Awaiting creator',
      icon: <HourglassTopIcon />,
      ...(connectionsDown ? unavailable(connections) : {
        value: connections.awaitingCreator,
        helpText: 'Requests the creator has not answered yet',
        color: connections.awaitingCreator > 0 ? 'warning' : 'primary',
      }),
    },
    {
      key: 'requests',
      to: '/connections',
      title: 'New requests (24h)',
      icon: <AddLinkIcon />,
      ...(connectionsDown ? unavailable(connections) : {
        value: connections.requested24h,
        helpText: `${connections.requestedRecent} requested and ${connections.acceptedRecent} accepted in the last ${windowDays} days`,
      }),
    },
    {
      key: 'cashouts',
      to: '/redemptions',
      title: 'Cash-outs to pay',
      icon: <PaidIcon />,
      ...(cashoutsDown ? unavailable(cashouts) : {
        value: cashouts.pending,
        helpText: cashouts.pending > 0 ? (owed || 'Waiting to be paid') : 'Nobody is waiting to be paid',
        color: cashouts.pending > 0 ? 'warning' : 'primary',
      }),
    },
  ];
}

export default function LiveActivityTiles() {
  const live = useLiveActivity();
  const tiles = buildTiles(live);

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mb: 1 }}>
        <Typography variant="h6">Right now</Typography>
        {live.data?.asOf && (
          <Typography variant="caption" color="text.secondary">
            as of {dayjs(live.data.asOf).format('h:mm A')} · updates every minute
          </Typography>
        )}
      </Box>
      <Grid container spacing={2}>
        {tiles.map(({ key, to, ...tile }) => (
          <Grid item xs={12} sm={6} md={3} key={key}>
            <Box component={Link} to={to} sx={{ display: 'block', textDecoration: 'none' }} data-tile={key}>
              <StatCard {...tile} />
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}
